import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "convex/react";
import { api } from "@convex/_generated/api";
import type { Id } from "@convex/_generated/dataModel";
import { useAdminAuth } from "@/features/admin/hooks/useAdminAuth";
import { AdminShell } from "@/features/admin/shell/AdminShell";
import { PlayersTable } from "@/features/admin/players/components/PlayersTable";
import { useT } from "@/shared/i18n";
import { Skeleton } from "@/shared/ui/Skeleton";

export const Route = createFileRoute("/_adminGuarded/admin/teams/$teamId")({
  component: AdminTeamPlayersPage,
});

function AdminTeamPlayersPage() {
  const t = useT();
  const { teamId } = Route.useParams();
  const { state } = useAdminAuth();
  const token = state.status === "authenticated" ? state.token : null;

  const data = useQuery(
    api.admin.getTeamPlayers,
    token ? { token, teamId: teamId as Id<"knbsbTeams"> } : "skip",
  );

  const breadcrumbs =
    data === undefined
      ? [{ label: t("admin.teams.title"), to: "/admin" }, { label: t("admin.teams.loading") }]
      : [{ label: t("admin.teams.title"), to: "/admin" }, { label: data.team.name }];

  return (
    <AdminShell breadcrumbs={breadcrumbs}>
      {data === undefined ? (
        <div className="flex flex-col gap-3">
          <Skeleton className="h-9 w-1/3" />
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-12 w-full" />
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h1 className="font-display text-2xl lg:text-3xl font-semibold tracking-tight text-ink leading-tight">
              {data.team.name}
            </h1>
            <p className="text-muted mt-1 text-sm max-w-prose">
              {t("admin.teams.detailSubtitle", { count: data.rows.length })}
            </p>
          </div>
          <PlayersTable rows={data.rows} />
        </>
      )}
    </AdminShell>
  );
}
